import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { X, Mail, Lock, User, AlertCircle, CheckCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthModal = ({ isOpen, onClose }) => {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register" | "verify-pending" | "resend"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(email, password);
        onClose();
      } else if (mode === "register") {
        const result = await register(email, password, name);
        if (result.email_verified === false) {
          setMode("verify-pending");
          setSuccess("Un email de vérification a été envoyé à " + email);
        } else {
          onClose();
        }
      }
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      // Check if it's an email verification error
      if (err.response?.status === 403 && errorMsg.includes("non vérifié")) {
        setMode("verify-pending");
        setError(errorMsg);
      } else {
        setError(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      setSuccess("Un nouveau lien de vérification a été envoyé.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  // Email verification pending screen
  if (mode === "verify-pending") {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="auth-modal-overlay">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="auth-modal-verify">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white relative">
            <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <Mail className="h-8 w-8" />
              <div>
                <h2 className="text-xl font-bold">Vérifiez votre email</h2>
                <p className="text-sm text-emerald-100 mt-1">{email}</p>
              </div>
            </div>
          </div>

          <div className="p-6 text-center">
            <div className="bg-emerald-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-emerald-600" />
            </div>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              Un email de vérification a été envoyé
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Cliquez sur le lien dans l'email pour activer votre compte. 
              Le lien expire dans 24 heures.
            </p>

            {success && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg mb-4">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                {success}
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button
              onClick={handleResendVerification}
              disabled={submitting}
              variant="outline"
              className="w-full mb-3 gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${submitting ? 'animate-spin' : ''}`} />
              Renvoyer l'email
            </Button>

            <button
              onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
              className="text-sm text-blue-600 hover:underline"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="auth-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="auth-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white" data-testid="auth-modal-close">
            <X className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-bold">
            {mode === "login" ? "Connexion" : "Créer un compte"}
          </h2>
          <p className="text-sm text-blue-100 mt-1">
            {mode === "login"
              ? "Connectez-vous pour accéder à vos validations"
              : "Inscrivez-vous gratuitement"}
          </p>
        </div>

        <div className="p-6">
          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 rounded-xl py-3 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all mb-4"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continuer avec Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">ou</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="auth-name-input"
                  type="text"
                  placeholder="Nom"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="auth-email-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="auth-password-input"
                type="password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="auth-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="auth-submit-button"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2.5 rounded-xl"
            >
              {submitting ? "..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          {/* Toggle */}
          <p className="text-center text-sm text-slate-500 mt-4">
            {mode === "login" ? (
              <>Pas de compte ? <button onClick={() => { setMode("register"); setError(""); }} className="text-blue-600 font-medium hover:underline" data-testid="switch-to-register">Inscrivez-vous</button></>
            ) : (
              <>Déjà un compte ? <button onClick={() => { setMode("login"); setError(""); }} className="text-blue-600 font-medium hover:underline" data-testid="switch-to-login">Connectez-vous</button></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
